<template>
    <modal
        v-if="show && showV1Modal"
        :form="form"
        :size="hideFooter ? 'is-middle' : 'is-small'"
        :type="'is-primary'"
        :title="'Upgrade'"
        :hide-foot="hideFooter"
        :success-text="'Upgrade Successful'"
        :backText="backButtonText"
        :cancelText="cancelButtonText"
        class="upgradePlan__modal trial"
        :class="{
            'upgradePlan__modal-old': checkOldBillingPlans,
        }"
        :submitText="submitButtonText"
        :submitClasses="{
            'is-primary': true,
            'is-busy': loadingButton,
        }"
        :backClasses="{
            'is-busy': loadingBackButton,
        }"
        :submitDisabled="submitButtonDisabled"
        :hideBack="!showBackButton"
        :hideCancel="!showCancelButton"
        @back="handleBack"
        @closed="handleClose"
        @submit="nextStep"
    >
        <configuration-stepper :type="'simple'" :currentStep="currentStep" :steps="steps" />
        <div v-if="loading || loading10Dls">
            <basic-loader :center="true" aria-label="UpgradePlanTrialModal" />
        </div>
        <div v-else class="trial__content">
            <template
                v-if="
                    [
                        MODAL_TYPES.UPGRADE_PLAN_BEFORE,
                        MODAL_TYPES.UPGRADE_PLAN,
                        MODAL_TYPES.UPGRADE_PLAN_INFO,
                    ].includes(currentStep)
                "
            >
                <upgrade-plan
                    ref="upgradePlan"
                    :MODAL_TYPE="currentStep"
                    :MODAL_TYPES="MODAL_TYPES"
                    :tablePrices="tablePrices"
                    @handle-change-upgrade-plan="handleChangeUpgradePlan"
                    @handle-change-upgrade-plan-terms="handleChangeUpgradePlanTerms"
                />
            </template>
            <template v-if="currentStep === MODAL_TYPES.UPGRADE_PLAN_INFO">
                <div v-if="showPaymentMethod" class="trial__column">
                    <div class="field">
                        <label class="label">Payment Information</label>

                        <p class="control">
                            <form-dropdown
                                v-if="sortedBillingSources.length"
                                :key="sortedBillingSources.length"
                                v-model="formCard.backup"
                                :hideLabelColon="true"
                                :placeholder="'Select Payment Method'"
                                :error="formCard.errors.get('backup')"
                                :settings="cardDropdownAutoRechargeSettings"
                                :disabled="loading || loading10Dls"
                            />
                        </p>
                    </div>

                    <div class="field">
                        <a class="button" @click="handleShowNewPaymentMethod">
                            Add Payment Method
                        </a>
                    </div>
                </div>
            </template>
        </div>
    </modal>
</template>

<script>
import { mapState } from 'pinia';
import stores from 'store/modules';
import Form from 'core/Form';
import EarlyUpgradeMixin from '../../mixins/early-upgrade';
import OrganizationMixin from '../../mixins/organization';
import UpgradePlan from './UpgradePlan/UpgradePlan.vue';
import CardsMixin from 'mixins/cards';

const MODAL_TYPES = {
    UPGRADE_PLAN: 1,
    UPGRADE_PLAN_BEFORE: 2,
    UPGRADE_PLAN_INFO: 3,
};

export default {
    emits: ['close'],

    components: {
        UpgradePlan,
    },

    mixins: [EarlyUpgradeMixin, OrganizationMixin, CardsMixin],

    data() {
        return {
            form: new Form({}),
            hideFooter: false,
            showBackButton: false,
            showCancelButton: true,
            hasPlanDowngrade: false,
            loading: false,
            loading10Dls: false,
            loadingBackButton: false,
            steps: 1,
            currentStepIndex: 0,
            MODAL_TYPES: MODAL_TYPES,
            showError: false,
            paymentStepLoading: false,
            upgrade_plan: {
                plan: '',
                type: '',
                price: 0,
                hasPlanDowngrade: false,
                showPreviewPlan: false,
                hasCard: false,
            },
            formCard: new Form(
                {
                    name: '',
                    email: '',
                    token: '',
                    primary: false,
                    backup: false,
                    type: 'default',
                    confirm: false,
                },
                false
            ),
            newCardForm: new Form(
                {
                    name: '',
                    email: '',
                    card_token: '',
                    primary: false,
                    backup: false,
                    type: 'default',
                    confirm: false,
                },
                false
            ),
            terms: false,
            showPaymentMethod: true,
        };
    },

    computed: {
        showV1Modal() {
            return !this.featureFlags?.billing_upgrade_v2;
        },

        show() {
            return this.upgradePlanTrialModalState;
        },

        isPFTTrialFlowCanada() {
            return (
                this.user.isPftTrialFlow &&
                this.isOnTrial &&
                this.config['10dlc_banners_info']?.is_messaging_canada_only
            );
        },

        isPFTTrialFlowToolFree() {
            return (
                this.user.isPftTrialFlow &&
                this.isOnTrial &&
                !this.config['10dlc_banners_info']?.is_messaging_canada_only &&
                this.isTollFree
            );
        },

        isTollFree() {
            return this.number.isTollFree;
        },

        isTollFreePending() {
            return (
                this.isTollFree &&
                (this.number.statusUnderVerification || this.number.statusInternalReview)
            );
        },

        isTollFreeApproved() {
            return this.isTollFree && this.number.statusTollFreeVerified;
        },

        isNoComplianceTollFree() {
            return this.isTollFree && !this.isTollFreePending && !this.isTollFreeApproved;
        },

        isDlsProfileCompleted() {
            return true;
        },

        isNoComplianceLocal() {
            return (
                !this.isPFTTrialFlowToolFree &&
                !this.isPFTTrialFlowCanada &&
                !this.isDlsProfileCompleted
            );
        },

        isComplianceLocalCompleted() {
            return (
                !this.isPFTTrialFlowToolFree &&
                !this.isPFTTrialFlowCanada &&
                this.isDlsProfileCompleted
            );
        },

        submitButtonDisabled() {
            return !this.terms && this.currentStep === MODAL_TYPES.UPGRADE_PLAN_BEFORE;
        },

        loadingButton() {
            return Boolean(this.form.busy || this.formCard.busy || this.paymentStepLoading);
        },

        stepsAccess() {
            if (this.isPFTTrialFlowCanada) {
                return [MODAL_TYPES.UPGRADE_PLAN, MODAL_TYPES.UPGRADE_PLAN_INFO];
            }

            if (this.isPFTTrialFlowToolFree) {
                if (this.isTollFreeApproved) {
                    return [MODAL_TYPES.UPGRADE_PLAN, MODAL_TYPES.UPGRADE_PLAN_INFO];
                }

                return [
                    MODAL_TYPES.UPGRADE_PLAN,
                    MODAL_TYPES.UPGRADE_PLAN_BEFORE,
                    MODAL_TYPES.UPGRADE_PLAN_INFO,
                ];
            }

            return [
                MODAL_TYPES.UPGRADE_PLAN,
                MODAL_TYPES.UPGRADE_PLAN_BEFORE,
                MODAL_TYPES.UPGRADE_PLAN_INFO,
            ];
        },

        currentStep() {
            return this.stepsAccess[this.currentStepIndex];
        },

        tablePrices() {
            return [];
        },

        submitButtonText() {
            if (this.currentStep === MODAL_TYPES.UPGRADE_PLAN_BEFORE) {
                return 'Upgrade Now';
            }

            if (this.currentStep === MODAL_TYPES.UPGRADE_PLAN_INFO) {
                if (
                    this.isNoComplianceTollFree ||
                    this.isTollFreePending ||
                    this.isPFTTrialFlowCanada
                ) {
                    return 'Upgrade Now';
                }

                return 'Upgrade and submit my application';
            }

            return 'Continue';
        },

        backButtonText() {
            if (this.currentStep === MODAL_TYPES.UPGRADE_PLAN_BEFORE) {
                if (this.isTollFreePending) {
                    return 'Wait for Approval';
                }

                return 'Complete Compliance First';
            }

            return 'Back';
        },

        cancelButtonText() {
            if (this.currentStep === MODAL_TYPES.UPGRADE_PLAN && this.isComplianceLocalCompleted) {
                return 'Test First';
            }

            return 'Cancel';
        },

        primary() {
            return this.billingSources.filter((item) => item.primary);
        },

        ...mapState(stores.Subscriptions, ['subscription']),
        ...mapState(stores.TollFreeAndLocalModalModule, ['upgradePlanTrialModalState']),
        ...mapState(stores.User, ['user', 'config']),
        ...mapState(stores.Number, ['number']),
        ...mapState(stores.Organization, ['featureFlags']),
        ...mapState(stores.DLCProfile, ['dlsProfile']),
    },

    watch: {
        show(status) {
            if (status) {
                this.init();
            } else {
                this.initReset();
            }
        },

        'sortedBillingSources.length'(value) {
            this.reInitDropdown();
        },
    },

    methods: {
        init() {
            if (!this.isTollFree) this.init10Dls();
            this.steps = this.stepsAccess.length;
        },

        initReset() {
            this.form.reset();
            this.showBackButton = false;
            this.showCancelButton = true;
            this.currentStepIndex = 0;
        },

        async init10Dls() {
            this.loading10Dls = true;

            let requests = [];

            requests = [this.$store.dispatch('getOrganization10DlcNew')];

            await Promise.all(requests);

            this.loading10Dls = false;
        },

        handleClose() {
            this.$store.dispatch('showUpgradePlanTrialModal', false);
            this.$emit('close');
        },

        setStep(step) {
            this.currentStepIndex = this.getStep(step);
            this.calculateButtons();
        },

        getStep(step) {
            return this.stepsAccess.findIndex((i) => i === step);
        },

        calculateButtons() {
            if (this.currentStep > 1) {
                this.showBackButton = true;
                this.showCancelButton = false;
            } else {
                this.showBackButton = false;
                this.showCancelButton = true;
            }
        },

        async nextStep() {
            if (this.currentStep === MODAL_TYPES.UPGRADE_PLAN) {
                if (
                    (this.isPFTTrialFlowToolFree && !this.isTollFreeApproved) ||
                    this.isNoComplianceLocal
                ) {
                    this.setStep(MODAL_TYPES.UPGRADE_PLAN_BEFORE);

                    return;
                }

                this.setStep(MODAL_TYPES.UPGRADE_PLAN_INFO);
                this.reInitDropdown();

                return;
            }

            if (this.currentStep === MODAL_TYPES.UPGRADE_PLAN_BEFORE) {
                this.setStep(MODAL_TYPES.UPGRADE_PLAN_INFO);
                this.reInitDropdown();

                return;
            }

            if (this.currentStep === MODAL_TYPES.UPGRADE_PLAN_INFO) {
                try {
                    this.form.busy = true;
                    this.paymentStepLoading = true;

                    if (this.$refs.upgradePlan) {
                        await this.$refs.upgradePlan.$refs.changePlanNew.save();
                    }

                    if (!this.showError) {
                        this.paymentStepLoading = false;
                    }
                } catch (e) {
                    this.$log.error(e, this.$options.__file, 'nextStep');
                } finally {
                    this.form.busy = false;
                    this.paymentStepLoading = false;
                    this.$store.dispatch('showUpgradePlanTrialModal', false);
                }
            }
        },

        handleBack() {
            if (this.currentStep === MODAL_TYPES.UPGRADE_PLAN_BEFORE) {
                this.loadingBackButton = true;

                if (this.isNoComplianceTollFree || this.isNoComplianceLocal) {
                    this.$store.dispatch('updateBusinessProfileModalState', true);
                    this.$router.replace('/settings/organization/compliance');
                    this.$store.dispatch('showUpgradePlanTrialModal', false);
                    this.loadingBackButton = false;

                    return;
                }

                this.loadingBackButton = false;
                this.handleClose();

                return;
            }

            if (this.currentStep === MODAL_TYPES.UPGRADE_PLAN_INFO) {
                if (this.isPFTTrialFlowToolFree) {
                    if (this.isNoComplianceTollFree || this.isTollFreePending) {
                        this.setStep(MODAL_TYPES.UPGRADE_PLAN_BEFORE);

                        return;
                    }

                    this.setStep(MODAL_TYPES.UPGRADE_PLAN);

                    return;
                }

                if (this.isNoComplianceLocal) {
                    this.setStep(MODAL_TYPES.UPGRADE_PLAN_BEFORE);
                }
            }
        },

        reInitDropdown() {
            if (this.primary && this.primary[0]) {
                this.formCard.backup = this.primary[0].id;
            }
        },

        handleChangeUpgradePlan([key, value]) {
            this.upgrade_plan[key] = value;
        },

        handleChangeUpgradePlanTerms(status) {
            this.terms = status;
        },

        handleShowNewPaymentMethod() {
            this.showPaymentMethods();
        },
    },
};
</script>
